/**
 * WifiApPortsPanel — Cableado del punto de acceso.
 *
 * Un AP se conecta por su uplink a una toma de pared (y de ahí al patch panel),
 * o directamente a un puerto de switch. Este panel muestra el estado de cada
 * puerto del AP y permite tender/quitar el cable sin salir de la ficha.
 *
 * El otro extremo puede ser:
 *   - una toma de pared        → port_b_type 'wall_outlet'
 *   - un puerto de switch      → port_b_type 'switch_port'
 *   - un puerto de patch panel → port_b_type 'panel_port'
 *
 * Props: ap, companyId, onChanged (se llama tras conectar/desconectar)
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { TrashIcon, BoltIcon } from '@heroicons/react/24/outline';
import api from '../services/api';

const PORT_TYPES = [
  { value: 'rj45',  label: 'RJ45' },
  { value: 'sfp',   label: 'SFP' },
  { value: 'sfp+',  label: 'SFP+' },
  { value: 'sfp28', label: 'SFP28' },
  { value: 'dac',   label: 'DAC' },
];

const SPEEDS = [100, 1000, 2500, 5000, 10000];

const CABLE_TYPES = [
  { value: 'cat5e',    label: 'Cat5e' },
  { value: 'cat6',     label: 'Cat6' },
  { value: 'cat6a',    label: 'Cat6a' },
  { value: 'cat7',     label: 'Cat7' },
  { value: 'fiber_mm', label: 'Fibra multimodo' },
  { value: 'fiber_sm', label: 'Fibra monomodo' },
  { value: 'dac',      label: 'DAC' },
];

const EMPTY_LINK = {
  kind: 'wall',        // wall | switch | panel
  outlet_id: '',
  element_id: '',
  port_id: '',
  cable_type: '',
  cable_color: '',
  cable_length_m: '',
  notes: '',
};

export default function WifiApPortsPanel({ ap, companyId, onChanged }) {
  const [ports, setPorts]           = useState([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');
  const [activePort, setActivePort] = useState(null);
  const [link, setLink]             = useState(EMPTY_LINK);
  const [saving, setSaving]         = useState(false);

  const [outlets, setOutlets]       = useState([]);
  const [switches, setSwitches]     = useState([]);
  const [panels, setPanels]         = useState([]);
  const [targetPorts, setTargetPorts] = useState([]);
  const [loadingTargets, setLoadingTargets] = useState(false);
  const [showAddPort, setShowAddPort] = useState(false);
  const [newPort, setNewPort]       = useState({ label: '', port_type: 'rj45', speed_mbps: 1000 });

  const isEditing = Boolean(ap?.id);

  const load = useCallback(async () => {
    if (!ap?.id || !companyId) return;
    setLoading(true);
    try {
      const res = await api.getWifiAPPorts(ap.id, companyId);
      setPorts(res.data || []);
    } catch (e) {
      setError(e.message || 'Error cargando los puertos del AP');
    } finally {
      setLoading(false);
    }
  }, [ap?.id, companyId]);

  useEffect(() => { load(); }, [load]);

  // Listas de posibles destinos
  useEffect(() => {
    if (!companyId) return;
    Promise.all([
      api.getWallOutlets(companyId).catch(() => ({ data: [] })),
      api.getElements(companyId, { element_type: 'switch' }).catch(() => ({ data: [] })),
      api.getElements(companyId, { element_type: 'panel' }).catch(() => ({ data: [] })),
    ]).then(([w, sw, pn]) => {
      setOutlets(w.data || []);
      setSwitches(sw.data || []);
      setPanels(pn.data || []);
    });
  }, [companyId]);

  // Puertos del elemento destino (switch / patch panel)
  useEffect(() => {
    const elId = link.element_id ? parseInt(link.element_id, 10) : null;
    if (!elId || (link.kind !== 'switch' && link.kind !== 'panel')) {
      setTargetPorts([]);
      return;
    }
    setLoadingTargets(true);
    api.getPorts(elId, companyId)
      .then(r => setTargetPorts(r.data || []))
      .catch(() => setTargetPorts([]))
      .finally(() => setLoadingTargets(false));
  }, [link.element_id, link.kind, companyId]);

  const openLink = (port) => {
    setActivePort(port);
    setError('');
    const conn = port.connection;
    if (!conn) {
      setLink(EMPTY_LINK);
      return;
    }
    const other = conn.other_end || {};
    const kind = other.port_type === 'wall_outlet' ? 'wall'
      : other.port_type === 'panel_port' ? 'panel' : 'switch';
    setLink({
      kind,
      outlet_id:      kind === 'wall'  ? String(other.port_id) : '',
      element_id:     kind === 'wall'  ? '' : String(other.element_id || ''),
      port_id:        kind === 'wall'  ? '' : String(other.port_id || ''),
      cable_type:     conn.cable_type || '',
      cable_color:    conn.cable_color || '',
      cable_length_m: conn.cable_length_m != null ? String(conn.cable_length_m) : '',
      notes:          conn.notes || '',
    });
  };

  const closeLink = () => { setActivePort(null); setLink(EMPTY_LINK); setError(''); };

  const saveLink = async () => {
    if (!activePort) return;
    setSaving(true); setError('');
    try {
      // Siempre se recrea el cable: el backend reemplaza el del puerto de origen
      if (activePort.connection) {
        await api.deleteConnection(activePort.connection.id, companyId);
      }
      if (!link.kind || (link.kind === 'wall' ? !link.outlet_id : !link.port_id)) {
        await load(); onChanged?.(); closeLink();
        return;
      }
      const body = {
        port_a_type: 'wifi_ap_port',
        port_a_id: activePort.id,
        ...(link.kind === 'wall'
          ? { port_b_type: 'wall_outlet', port_b_id: parseInt(link.outlet_id, 10) }
          : {
              port_b_type: link.kind === 'panel' ? 'panel_port' : 'switch_port',
              port_b_id: parseInt(link.port_id, 10),
            }),
        cable_type:     link.cable_type || undefined,
        cable_color:    link.cable_color || undefined,
        cable_length_m: link.cable_length_m !== '' ? parseFloat(link.cable_length_m) : undefined,
        notes:          link.notes || undefined,
      };
      await api.createConnection(body, companyId);
      await load();
      onChanged?.();
      closeLink();
    } catch (e) {
      setError(e.message || 'No se pudo guardar el cable');
    } finally {
      setSaving(false);
    }
  };

  const removeLink = async () => {
    if (!activePort?.connection) return;
    setSaving(true); setError('');
    try {
      await api.deleteConnection(activePort.connection.id, companyId);
      await load();
      onChanged?.();
      closeLink();
    } catch (e) {
      setError(e.message || 'No se pudo quitar el cable');
    } finally {
      setSaving(false);
    }
  };

  const addPort = async () => {
    setSaving(true); setError('');
    try {
      await api.createWifiAPPort(
        ap.id,
        {
          label: newPort.label.trim() || null,
          port_type: newPort.port_type,
          speed_mbps: newPort.speed_mbps ? Number(newPort.speed_mbps) : null,
        },
        companyId,
      );
      setShowAddPort(false);
      setNewPort({ label: '', port_type: 'rj45', speed_mbps: 1000 });
      await load();
    } catch (e) {
      setError(e.message || 'No se pudo añadir el puerto');
    } finally {
      setSaving(false);
    }
  };

  const removePort = async (port) => {
    if (!window.confirm(`¿Eliminar el puerto ${port.port_number} del AP?`)) return;
    setSaving(true); setError('');
    try {
      await api.deleteWifiAPPort(ap.id, port.id, companyId);
      await load();
    } catch (e) {
      setError(e.message || 'No se pudo eliminar el puerto');
    } finally {
      setSaving(false);
    }
  };

  const targetElements = useMemo(
    () => (link.kind === 'panel' ? panels : switches),
    [link.kind, panels, switches],
  );

  if (!isEditing) {
    return (
      <p className="dv-hint">
        Guarda el punto de acceso antes de poder cablearlo.
      </p>
    );
  }

  return (
    <div>
      {error && (
        <div className="alert alert-danger py-2 px-3 mb-2" style={{ fontSize: '0.85em' }}>{error}</div>
      )}

      <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
        <span className="fw-semibold small">Puertos del AP</span>
        <span className="badge text-bg-secondary" style={{ fontSize: '0.68em' }}>{ports.length}</span>
        <div className="ms-auto d-flex gap-2">
          <button className="btn btn-outline-secondary btn-sm" onClick={load} disabled={loading}>↺</button>
          <button className="btn btn-outline-primary btn-sm" onClick={() => setShowAddPort(s => !s)}>
            ＋ Puerto
          </button>
        </div>
      </div>

      {showAddPort && (
        <div className="card mb-3">
          <div className="card-body py-2">
            <div className="row g-2 align-items-end">
              <div className="col-md-4">
                <label className="form-label small mb-1">Etiqueta</label>
                <input className="form-control form-control-sm" placeholder="Pass-through"
                  value={newPort.label} onChange={e => setNewPort(p => ({ ...p, label: e.target.value }))} />
              </div>
              <div className="col-md-3">
                <label className="form-label small mb-1">Tipo</label>
                <select className="form-select form-select-sm" value={newPort.port_type}
                  onChange={e => setNewPort(p => ({ ...p, port_type: e.target.value }))}>
                  {PORT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label small mb-1">Velocidad (Mbps)</label>
                <select className="form-select form-select-sm" value={newPort.speed_mbps}
                  onChange={e => setNewPort(p => ({ ...p, speed_mbps: e.target.value }))}>
                  <option value="">—</option>
                  {SPEEDS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="col-md-2">
                <button className="btn btn-success btn-sm w-100" onClick={addPort} disabled={saving}>Añadir</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {loading && <p className="text-muted small">Cargando…</p>}

      {!loading && (
        <div className="card shadow-sm mb-3">
          <div className="card-body p-0">
            <table className="table table-sm table-hover mb-0" style={{ fontSize: '0.85em' }}>
              <thead className="table-light">
                <tr>
                  <th style={{ width: 60 }}>Puerto</th>
                  <th>Etiqueta</th>
                  <th style={{ width: 120 }}>Tipo</th>
                  <th>Conectado a</th>
                  <th style={{ width: 150 }}>Cable</th>
                  <th style={{ width: 130 }}></th>
                </tr>
              </thead>
              <tbody>
                {ports.map(port => {
                  const other = port.connection?.other_end;
                  return (
                    <tr key={port.id}>
                      <td><span className="badge text-bg-primary" style={{ fontFamily: 'monospace' }}>P{port.port_number}</span></td>
                      <td>{port.label || <span className="text-muted">—</span>}</td>
                      <td className="text-muted">{port.port_type}{port.speed_mbps ? ` · ${port.speed_mbps >= 1000 ? `${port.speed_mbps / 1000}G` : `${port.speed_mbps}M`}` : ''}</td>
                      <td>
                        {other ? (
                          <span>
                            {other.port_type === 'wall_outlet' ? '🏢' : other.port_type === 'panel_port' ? '🪞' : '🔀'}{' '}
                            <strong>{other.element_name || other.label}</strong>
                            {other.label && other.port_type !== 'wall_outlet' && <span className="text-muted"> · {other.label}</span>}
                            {other.room && <span className="text-muted"> ({other.room})</span>}
                          </span>
                        ) : (
                          <span className="text-muted">Sin cable</span>
                        )}
                      </td>
                      <td className="text-muted">
                        {port.connection
                          ? [port.connection.cable_type, port.connection.cable_length_m ? `${port.connection.cable_length_m} m` : null].filter(Boolean).join(' · ') || '—'
                          : '—'}
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <button className="btn btn-outline-secondary btn-sm py-0 px-2"
                            onClick={() => openLink(port)}>
                            {port.connection ? 'Cambiar' : 'Conectar'}
                          </button>
                          {!port.connection && ports.length > 1 && (
                            <button className="btn btn-outline-danger btn-sm py-0 px-1" title="Eliminar puerto"
                              onClick={() => removePort(port)}>🗑</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {ports.length === 0 && (
                  <tr><td colSpan={6} className="text-muted text-center py-3">Este AP no tiene puertos.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Editor del cable ── */}
      {activePort && (
        <div className="card border-primary mb-2">
          <div className="card-header py-2 d-flex align-items-center">
            <strong style={{ fontSize: '0.9em' }}>
              Cable del puerto {activePort.port_number}{activePort.label ? ` · ${activePort.label}` : ''}
            </strong>
            <button className="btn-close ms-auto" onClick={closeLink} />
          </div>
          <div className="card-body py-2">
            <div className="row g-2">
              <div className="col-md-4">
                <label className="form-label small mb-1">Conectar a</label>
                <select className="form-select form-select-sm" value={link.kind}
                  onChange={e => setLink(l => ({ ...l, kind: e.target.value, outlet_id: '', element_id: '', port_id: '' }))}>
                  <option value="wall">🏢 Toma de pared</option>
                  <option value="switch">🔀 Puerto de switch</option>
                  <option value="panel">🪞 Puerto de patch panel</option>
                </select>
              </div>

              {link.kind === 'wall' ? (
                <div className="col-md-5">
                  <label className="form-label small mb-1">Toma</label>
                  <select className="form-select form-select-sm" value={link.outlet_id}
                    onChange={e => setLink(l => ({ ...l, outlet_id: e.target.value }))}>
                    <option value="">— sin cable —</option>
                    {outlets.map(o => (
                      <option key={o.id} value={o.id}>
                        {o.code}{o.name ? ` · ${o.name}` : ''}{o.room ? ` (${o.room})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <>
                  <div className="col-md-5">
                    <label className="form-label small mb-1">{link.kind === 'panel' ? 'Patch panel' : 'Switch'}</label>
                    <select className="form-select form-select-sm" value={link.element_id}
                      onChange={e => setLink(l => ({ ...l, element_id: e.target.value, port_id: '' }))}>
                      <option value="">— selecciona —</option>
                      {targetElements.map(el => <option key={el.id} value={el.id}>{el.name || el.code}</option>)}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label small mb-1">Puerto</label>
                    <select className="form-select form-select-sm" value={link.port_id}
                      onChange={e => setLink(l => ({ ...l, port_id: e.target.value }))}
                      disabled={!link.element_id || loadingTargets}>
                      <option value="">— sin cable —</option>
                      {targetPorts.map(p => {
                        const busy = p.connection && String(p.id) !== String(link.port_id);
                        return (
                          <option key={p.id} value={p.id} disabled={busy}>
                            P{p.port_number}{p.label ? ` · ${p.label}` : ''}{busy ? ' 🔴 ocupado' : ''}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </>
              )}
            </div>

            <div className="row g-2 mt-1">
              <div className="col-md-3">
                <label className="form-label small mb-1">Tipo de cable</label>
                <select className="form-select form-select-sm" value={link.cable_type}
                  onChange={e => setLink(l => ({ ...l, cable_type: e.target.value }))}>
                  <option value="">—</option>
                  {CABLE_TYPES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label small mb-1">Color</label>
                <input className="form-control form-control-sm" value={link.cable_color}
                  onChange={e => setLink(l => ({ ...l, cable_color: e.target.value }))} />
              </div>
              <div className="col-md-2">
                <label className="form-label small mb-1">Longitud (m)</label>
                <input className="form-control form-control-sm" type="number" step="0.1" value={link.cable_length_m}
                  onChange={e => setLink(l => ({ ...l, cable_length_m: e.target.value }))} />
              </div>
              <div className="col-md-4">
                <label className="form-label small mb-1">Notas</label>
                <input className="form-control form-control-sm" value={link.notes}
                  onChange={e => setLink(l => ({ ...l, notes: e.target.value }))} />
              </div>
            </div>

            {ap?.poe_powered && link.kind !== 'wall' && (
              <div className="form-text mt-2" style={{ fontSize: '0.78em' }}>
                <BoltIcon style={{ width: 13, height: 13, verticalAlign: '-2px' }} /> El AP está marcado como alimentado
                por PoE: si lo conectas a un puerto de switch, actívale también el PoE en ese puerto.
              </div>
            )}

            <div className="d-flex gap-2 justify-content-end mt-3">
              {activePort.connection && (
                <button className="btn btn-outline-danger btn-sm" onClick={removeLink} disabled={saving}>
                  <TrashIcon style={{ width: 14, height: 14 }} /> Quitar cable
                </button>
              )}
              <button className="btn btn-outline-secondary btn-sm" onClick={closeLink} disabled={saving}>Cancelar</button>
              <button className="btn btn-primary btn-sm" onClick={saveLink} disabled={saving}>
                {saving ? 'Guardando…' : '💾 Guardar cable'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
